import React, { useMemo, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { connect } from 'react-redux';
import { BulkLearnerActivityUploadForm } from '../../components/BulkLearnerActivityUploadForm';
import { BulkLearnerActivityUploadsTable } from '../../components/BulkLearnerActivityUploadsTable';
import BulkLearnerActivityUploadDateFilter from '../../components/BulkLearnerActivityUploadsTable/BulkLearnerActivityUploadDateFilter';
import Text, { TextStyle } from '../../components/Text';
import {
  changeBulkLearnerActivityUploadPage,
  ChangeBulkLearnerActivityUploadPageActionCreator,
  fetchBulkLearnerActivityUploads,
  FetchBulkLearnerActivityUploadsAsync,
} from '../../ducks/bulkLearnerActivityUploads';
import { getLastDaysDateRange } from '../../utilities/moment';
import DefaultPage from '../DefaultPage';
import { PageSection, PageSectionTitle } from '../PageStructure';

interface Props {
  changeBulkLearnerActivityUploadPage: ChangeBulkLearnerActivityUploadPageActionCreator;
  fetchBulkLearnerActivityUploads: FetchBulkLearnerActivityUploadsAsync;
}

const FIRST_PAGE = 1;
const DEFAULT_DATE_RANGE_DAYS = 30;

const BulkLearnerActivityUpload: React.FunctionComponent<Props> = ({
  changeBulkLearnerActivityUploadPage,
  fetchBulkLearnerActivityUploads,
}) => {
  const initialDateRange = useMemo(() => getLastDaysDateRange(DEFAULT_DATE_RANGE_DAYS), []);

  const [startDate, setStartDate] = useState<string | undefined>(initialDateRange.startDate);
  const [endDate, setEndDate] = useState<string | undefined>(initialDateRange.endDate);
  const [dateFilterKey, setDateFilterKey] = useState(0);

  const handleDateChange = (newStartDate?: string, newEndDate?: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    changeBulkLearnerActivityUploadPage(FIRST_PAGE);
  };

  const handleUploadSuccess = () => {
    unstable_batchedUpdates(() => {
      setStartDate(initialDateRange.startDate);
      setEndDate(initialDateRange.endDate);
      setDateFilterKey((key) => key + 1);
      changeBulkLearnerActivityUploadPage(FIRST_PAGE);
    });
    fetchBulkLearnerActivityUploads(FIRST_PAGE, undefined, initialDateRange.startDate, initialDateRange.endDate);
  };

  return (
    <DefaultPage title="Bulk Learner Activity Upload">
      <PageSection>
        <PageSectionTitle>
          <Text tag="h2" bold={true} textStyle={TextStyle.SectionTitle}>
            Upload CSV
          </Text>
        </PageSectionTitle>
        <Text textStyle={TextStyle.SubTitle}>Here you can upload a CSV</Text>
        <BulkLearnerActivityUploadForm onUploadSuccess={handleUploadSuccess} />
      </PageSection>
      <PageSection>
        <PageSectionTitle>
          <Text tag="h2" bold={true} textStyle={TextStyle.SectionTitle}>
            Ingested CSVs
          </Text>
        </PageSectionTitle>
        <Text textStyle={TextStyle.SubTitle}>Here you can download Successes and Errors and see the stats for an ingestion</Text>
        <BulkLearnerActivityUploadDateFilter
          key={dateFilterKey}
          onDateChange={handleDateChange}
          initialStartDate={initialDateRange.startDate}
          initialEndDate={initialDateRange.endDate}
        />
        <BulkLearnerActivityUploadsTable startDate={startDate} endDate={endDate} />
      </PageSection>
    </DefaultPage>
  );
};

const mapDispatchToProps = {
  changeBulkLearnerActivityUploadPage,
  fetchBulkLearnerActivityUploads,
};

export default connect(null, mapDispatchToProps)(BulkLearnerActivityUpload);
